<template>
  <figure ref="root" class="view-gallery-live-stage index-ignore" :class="{ 'is-compact': compact, 'is-embedded': embedded }" :style="{ '--demo-aspect': `${designWidth}/${designHeight}` }">
    <ExpandableProjectMedia :disabled="embedded" :title="`${activeLabel} demonstration`" :aspect-ratio="designWidth/designHeight" @change="onEnlarged">
    <div ref="canvas" class="view-gallery-live-canvas demo-open-surface" :aria-busy="frameMounted && !frameReady">
      <iframe inert
        v-if="frameMounted"
        ref="frame"
        :key="frameKey"
        :src="frameUrl"
        :style="frameStyle"
        :title="`${activeLabel} live demonstration`"
        tabindex="-1"
        allow="autoplay 'none'; fullscreen"
        @load="onFrameLoad"
      ></iframe>
      <div class="view-gallery-live-poster" :class="{ 'is-ready': frameReady, 'needs-activation': requiresActivation }" :aria-hidden="!requiresActivation">
        <img :src="posterSource" alt="" aria-hidden="true">
        <div v-if="requiresActivation">
          <span>The View Gallery</span>
          <i></i>
          <button type="button" @click="activateFrame">Load demonstration</button>
        </div>
      </div>
      <DemoOpenLink v-if="showOpenLink" :href="openUrl" :label="`Open ${activeLabel.toLowerCase()}`" />
    </div>
    <template #caption><figcaption v-if="!embedded">
      <div v-if="!mode" class="view-gallery-live-modes" role="tablist" aria-label="Choose a live Gallery scene">
        <button
          v-for="mode in modes"
          :key="mode.id"
          type="button"
          role="tab"
          :aria-selected="activeMode === mode.id"
          @click="selectMode(mode.id)"
        >
          <span>{{ mode.label }}</span>
          <small>{{ mode.copy }}</small>
        </button>
      </div>
      <p v-if="mode" class="view-gallery-caption">{{ activeMode === 'gallery' ? 'Explore the collection or bring a question of your own.' : 'Follow the narrative through its original works.' }}</p>
      <div class="view-gallery-live-actions">
        <button type="button" class="view-gallery-live-transport" :aria-label="darkDemo ? 'Use light theme in demonstration' : 'Use dark theme in demonstration'" :aria-pressed="darkDemo" @click="themeOverride = !darkDemo">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path v-if="!darkDemo" d="M15.8 12A6.7 6.7 0 0 1 8 4.2a6.7 6.7 0 1 0 7.8 7.8Z"/><g v-else><circle cx="10" cy="10" r="3.3"/><path d="M10 1v2m0 14v2M1 10h2m14 0h2M3.6 3.6 5 5m10 10 1.4 1.4M3.6 16.4 5 15M15 5l1.4-1.4"/></g></svg>
        </button>
        <button type="button" class="view-gallery-live-transport" :aria-label="requiresActivation ? 'Play demonstration' : paused ? 'Continue demonstration' : 'Pause demonstration'" @click="togglePlayback">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path v-if="paused || requiresActivation" d="m7 4 8 6-8 6Z"/><path v-else d="M7 4v12M13 4v12"/></svg>
        </button>
      </div>
    </figcaption></template>
    </ExpandableProjectMedia>
  </figure>
</template>

<script>
import { demoPreloadMargin, DEMO_RETAIN_MS } from '../../utils/demoPreload';
import DemoOpenLink from './DemoOpenLink.vue';
import ExpandableProjectMedia from './ExpandableProjectMedia.vue';
const DEMO_VIEW_ID = 'PNZPdB__yBeyYjkF';
const DEMO_WIDTH = 1440;
const DEMO_HEIGHT = 810;

export default {
  name: 'ViewGalleryLiveStage',
  components: { DemoOpenLink, ExpandableProjectMedia },
  emits: ['scene-change','playback-change'],
  props: {
    compact: Boolean,
    embedded: Boolean,
    mode: { type: String, default: '', validator: value => !value || ['gallery', 'exhibition'].includes(value) },
    initialMode: { type: String, default: 'gallery', validator: value => ['gallery', 'exhibition'].includes(value) },
    active: { type: Boolean, default: true },
    designWidth: { type: Number, default: 1440 },
    designHeight: { type: Number, default: 810 },
    showControls: Boolean,
    showOpenLink: { type: Boolean, default: true },
    eager: Boolean,
  },
  data() {
    return {
      activeMode: this.mode || this.initialMode,
      nearViewport: false,
      inViewport: false,
      sceneArea: 'hall',
      pageVisible: true,
      frameActivated: false,
      constrainedDevice: false,
      frameScale: 1,
      paused: false,
      pageDark: document.body.classList.contains('dark-mode'),
      themeOverride: null,
      enlarged: false,
      frameReady: false,
      frameKey: 0,
      modes: [
        { id: 'gallery', label: 'Gallery', copy: 'Move through the shared Hall' },
        { id: 'exhibition', label: 'Exhibition', copy: 'Follow one generated View' },
      ],
    };
  },
  computed: {
    darkDemo(){return this.themeOverride ?? this.pageDark;},
    requiresActivation() { return this.constrainedDevice && !this.frameActivated; },
    frameMounted() { return (this.eager || this.enlarged || (this.active && this.nearViewport)) && !this.requiresActivation; },
    activeLabel() { return this.activeMode === 'gallery' ? 'Gallery' : 'Exhibition'; },
    posterSource() { return this.activeMode === 'gallery' ? `/assets/img/projects/gallery-experience/hall-across-${this.darkDemo ? 'dark' : 'light'}.webp` : '/assets/img/view-gallery/exhibition-controls.webp?v=20260927-3'; },
    channel() { return `view-gallery-live-${this.$.uid}`; },
    frameUrl() {
      const path = '/views';
      const showcase = this.activeMode === 'gallery' ? 'tour' : 'exhibition';
      const controls = this.activeMode === 'exhibition' || this.showControls ? '&showcaseControls=1' : '';
      const view = this.activeMode === 'exhibition' ? `&showcaseView=${DEMO_VIEW_ID}` : '';
      return `${window.location.pathname}#${path}?showcase=${showcase}&galleryFrame=1&showcaseLoop=1${controls}${view}&showcaseChannel=${this.channel}`;
    },
    openUrl() {
      const path = this.activeMode === 'gallery' ? '/views' : `/view/${DEMO_VIEW_ID}`;
      return `${window.location.pathname}#${path}?presentation=museum&temporaryPresentation=1`;
    },
    effectivePaused() { return this.paused || !this.pageVisible || !this.active || (!this.enlarged && !this.inViewport); },
    frameStyle() {
      return {
        width: `${this.designWidth || DEMO_WIDTH}px`,
        height: `${this.designHeight || DEMO_HEIGHT}px`,
        transform: `scale(${this.frameScale})`,
      };
    },
  },
  watch: {
    paused(value) { this.$emit('playback-change', value); },
    darkDemo() { this.sendTheme(); this.publishScene(); },
    effectivePaused() { this.sendPlaybackState(); this.publishScene(); },
    frameReady() { this.publishScene(); },
    frameMounted(value) { if (!value) this.frameReady = false; },
    mode(value) {
      if (!value || value === this.activeMode) return;
      this.activeMode = value;
      this.frameActivated = true;
      this.frameReady = false;
      this.paused = false;
      this.frameKey += 1;
    },
  },
  mounted() {
    window.addEventListener('message', this.onSceneReady);
    this._themeObserver=new MutationObserver(()=>{const dark=document.body.classList.contains('dark-mode');if(dark!==this.pageDark){this.pageDark=dark;this.themeOverride=null;}});
    this._themeObserver.observe(document.body,{attributes:true,attributeFilter:['class']});
    this.constrainedDevice = this.isConstrainedDevice();
    this.frameActivated = !this.constrainedDevice;
    this._resizeObserver = new ResizeObserver(() => this.measureFrame());
    this._resizeObserver.observe(this.$refs.canvas);
    this.measureFrame();
    this._visibility = () => { this.pageVisible = !document.hidden; };
    document.addEventListener('visibilitychange', this._visibility);
    this._observer = new IntersectionObserver(([entry]) => {
      const near = Boolean(entry?.isIntersecting);
      if (near) {
        clearTimeout(this._unmountTimer);
        this.nearViewport = true;
        return;
      }
      clearTimeout(this._unmountTimer);
      this._unmountTimer = setTimeout(() => {
        this.nearViewport = false;
        if (!this.eager && !this.enlarged) this.frameReady = false;
      }, DEMO_RETAIN_MS);
    }, { rootMargin: demoPreloadMargin(), threshold: 0 });
    this._observer.observe(this.$refs.root);
    this._playbackObserver = new IntersectionObserver(([entry]) => {
      this.inViewport = Boolean(entry?.isIntersecting && entry.intersectionRatio >= .12);
    }, {threshold:[0,.12]});
    this._playbackObserver.observe(this.$refs.canvas);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.onSceneReady);
    this._themeObserver?.disconnect();
    clearTimeout(this._unmountTimer);
    this._resizeObserver?.disconnect();
    this._observer?.disconnect();
    this._playbackObserver?.disconnect();
    document.removeEventListener('visibilitychange', this._visibility);
  },
  methods: {
    onEnlarged(value) {
      this.enlarged=value;
      if(value && this.requiresActivation) this.activateFrame();
    },
    onSceneReady(event) {
      if (event.origin !== location.origin || event.source !== this.$refs.frame?.contentWindow || event.data?.channel !== this.channel) return;
      if (event.data.type === 'view-gallery-showcase-ready') { this.frameReady = true; this.sendTheme(); this.sendPlaybackState(); }
      if (event.data.type === 'view-gallery-showcase-place' && ['hall','directory','collection','compose','archive','about'].includes(event.data.area)) {
        this.sceneArea = event.data.area;
        this.publishScene();
      }
    },
    publishScene() { this.$emit('scene-change', { area:this.sceneArea, playing:this.activeMode==='gallery' && this.frameReady && !this.effectivePaused, dark:this.darkDemo }); },
    sendTheme() {
      this.$refs.frame?.contentWindow?.postMessage({ type:'view-gallery-showcase-theme', channel:this.channel, dark:this.darkDemo }, location.origin);
    },
    isConstrainedDevice() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },
    measureFrame() {
      const width = parseFloat(getComputedStyle(this.$refs.canvas).width) || this.designWidth || DEMO_WIDTH;
      this.frameScale = (width + 1) / (this.designWidth || DEMO_WIDTH);
    },
    activateFrame() {
      this.frameActivated = true;
      this.frameReady = false;
    },
    togglePlayback() {
      if (this.requiresActivation) { this.activateFrame(); this.paused = false; return; }
      this.paused = !this.paused;
    },
    selectMode(mode) {
      if (mode === this.activeMode && this.frameMounted) {
        this.paused = false;
        return;
      }
      this.activeMode = mode;
      this.frameActivated = true;
      this.frameReady = false;
      this.paused = false;
      this.frameKey += 1;
    },
    onFrameLoad() {
      this.sendTheme();
      this.sendPlaybackState();
    },
    sendPlaybackState() {
      this.$refs.frame?.contentWindow?.postMessage({
        type: 'view-gallery-showcase-playback',
        channel: this.channel,
        paused: this.effectivePaused,
      }, window.location.origin);
    },
  },
};
</script>

<style scoped>
.view-gallery-live-stage{margin:0;color:var(--site-text-primary)}
.view-gallery-live-canvas{position:relative;aspect-ratio:var(--demo-aspect,16/9);overflow:hidden;background:#24211f}
.view-gallery-live-canvas iframe{position:absolute;left:0;top:0;border:0;background:#24211f;pointer-events:none;transform-origin:top left}
.view-gallery-live-poster{position:absolute;inset:0;overflow:hidden;color:#f1ede4;background:var(--site-bg,#f5f3ee);opacity:1;pointer-events:none;transition:opacity 240ms ease-out}.view-gallery-live-poster.is-ready{opacity:0}.view-gallery-live-poster.needs-activation::after{content:"";position:absolute;inset:0;background:rgb(20 19 18 / 38%)}.view-gallery-live-poster>img{width:100%;height:100%;object-fit:cover}.view-gallery-live-poster>div{position:absolute;z-index:1;inset:0;display:grid;place-content:center;justify-items:center;gap:.7rem}
.view-gallery-live-poster>div{z-index:3;pointer-events:none}.view-gallery-live-poster button{pointer-events:auto}
.view-gallery-live-poster span{font:500 clamp(1.6rem,4vw,3.2rem)/1 "EB Garamond",serif}
.view-gallery-live-poster i{width:4rem;height:1px;background:currentColor;opacity:.45}
.view-gallery-live-poster button{padding:.65rem 0 .42rem;border:0;border-bottom:1px solid color-mix(in srgb,currentColor 40%,transparent);background:none;color:inherit;font:500 .7rem/1.3 "Quattrocento Sans",sans-serif;letter-spacing:.04em;cursor:var(--site-interactive-cursor)}
.view-gallery-caption{margin:0;max-width:32rem;font:13px/1.55 "Quattrocento Sans",sans-serif;color:var(--site-text-secondary)}
.view-gallery-live-stage figcaption{display:flex;align-items:start;justify-content:space-between;gap:1.5rem;margin-top:.85rem}
.view-gallery-live-modes{display:flex;min-width:0;gap:1.5rem}
.view-gallery-live-modes button{position:relative;display:grid;gap:.16rem;min-width:9rem;padding:.35rem 0 .65rem;border:0;border-bottom:1px solid color-mix(in srgb,currentColor 20%,transparent);background:none;color:var(--site-text-tertiary);text-align:left;cursor:var(--site-interactive-cursor);transition:color 180ms ease,border-color 180ms ease}
.view-gallery-live-modes button::after{content:"";position:absolute;left:0;right:0;bottom:-1px;height:1px;background:currentColor;transform:scaleX(0);transform-origin:right;transition:transform 220ms cubic-bezier(.23,1,.32,1)}
.view-gallery-live-modes button[aria-selected="true"]{color:var(--site-text-primary)}.view-gallery-live-modes button[aria-selected="true"]::after{transform:scaleX(1);transform-origin:left}
.view-gallery-live-modes span{font:500 .78rem/1.3 "Quattrocento Sans",sans-serif}.view-gallery-live-modes small{font:400 .68rem/1.35 "Quattrocento Sans",sans-serif;color:var(--site-text-tertiary)}
.view-gallery-live-actions{display:flex;align-items:center;gap:.35rem;flex:none;margin-left:auto}.view-gallery-live-actions a{color:inherit;font:500 .76rem/1.4 "Quattrocento Sans",sans-serif;text-decoration:underline;text-decoration-color:color-mix(in srgb,currentColor 35%,transparent);text-underline-offset:5px}.view-gallery-live-actions a span{display:inline-block;margin-left:.25rem;transition:transform 180ms cubic-bezier(.23,1,.32,1)}
.view-gallery-live-transport{display:grid;place-items:center;width:2rem;height:2rem;padding:0;border:0;border-radius:50%;background:none;color:var(--site-text-secondary);cursor:var(--site-interactive-cursor);transition:background 180ms ease,color 180ms ease}.view-gallery-live-transport svg{width:14px;height:14px;fill:none;stroke:currentColor;stroke-width:1.25;stroke-linecap:round;stroke-linejoin:round}
@media(hover:hover) and (pointer:fine){.view-gallery-live-modes button:hover{color:var(--site-text-primary)}.view-gallery-live-actions a:hover span{transform:translate(3px,-3px)}.view-gallery-live-transport:hover{background:color-mix(in srgb,currentColor 9%,transparent);color:var(--site-text-primary)}}
@media(max-width:700px){.view-gallery-caption{margin:0;max-width:32rem;font:13px/1.55 "Quattrocento Sans",sans-serif;color:var(--site-text-secondary)}
.view-gallery-live-stage figcaption{align-items:center}.view-gallery-live-modes{width:100%;gap:.7rem}.view-gallery-live-modes button{min-width:0;flex:1}.view-gallery-live-modes small{display:none}.view-gallery-live-actions a{display:none}}
@media(prefers-reduced-motion:reduce){.view-gallery-live-poster{transition:none}.view-gallery-live-modes button,.view-gallery-live-modes button::after,.view-gallery-live-actions a span{transition:none}}
.is-embedded { height:100%;margin:0; }.is-embedded :deep(.expandable-project-media),.is-embedded :deep(.project-media-viewer),.is-embedded :deep(.project-media-screen),.is-embedded .view-gallery-live-canvas { height:100%; }.is-embedded :deep(.project-media-caption){display:none;}
</style>
